// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableTree, TreeItems } from 'dnd-kit-sortable-tree';
import { ItemChangedReason } from 'dnd-kit-sortable-tree/dist/types';
import {
  Box,
  FormHelperText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
// Components, Layouts, Pages
import { ItemPackageProduct } from '~/components/module/package';
// Others
import { ITreeItem } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { formatCurrencyUSD } from '~/utils/helper/common';
import GroupContext from '~/context/groupContext/GroupContext';
import { IFormPackage, IPackageProduct, IProductItem } from '~/utils/interface/package';
// Styles, images, icons
import styles from './SetProductPackageForm.module.scss';

type Props = {
  detailPackage: IPackageProduct[];
};

const cx = classNames.bind(styles);

const SetProductPackageForm = (props: Props) => {
  //#region Destructuring Props
  const { detailPackage } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/packages', 'common/base']);

  const {
    setValue,
    watch,
    formState: { errors, isSubmitted },
  } = useFormContext<IFormPackage>();

  const { productItems, selectIdProductInventory, selectIdVariantInventory } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [items, setItems] = useState<TreeItems<ITreeItem<IPackageProduct>>>([]);
  //#endregion Declare State

  //#region Declare Memo
  const totalPrice = useMemo(
    () =>
      items
        .filter((item) => item.data?.isRequired)
        .reduce((sum, item) => sum + Number(item.data?.rentalPrice) * Number(item.data?.quantity), 0),
    [items]
  );
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (detailPackage.length <= 0) return;

    setItems(convertPackageToTreeItems(detailPackage));
  }, [detailPackage]);

  useEffect(() => {
    setValue(
      'packageItems',
      items.map((item) => item?.data as IPackageProduct),
      { shouldValidate: isSubmitted, shouldDirty: true }
    );
  }, [items]);

  useEffect(() => {
    setValue('rentalPrice', String(totalPrice || EMPTY_STRING));
  }, [totalPrice]);

  useEffect(() => {
    if (!productItems) return;

    // TODO: Handle Add Item Product Into Table And Update Quantity
    setItems((prev) => {
      const newItems = handleAddProductIntoTable(
        prev,
        productItems,
        selectIdProductInventory,
        selectIdVariantInventory
      );

      return newItems;
    });
  }, [JSON.stringify(productItems)]);
  //#endregion Implement Hook

  //#region Handle Function
  const convertPackageToTreeItems = (packageItems: IPackageProduct[]): TreeItems<ITreeItem<IPackageProduct>> => {
    return packageItems.map((item, index) => ({
      id: uid(),
      level: 1,
      order: index + 1,
      canHaveChildren: false,
      data: item,
    }));
  };

  const handleAddProductIntoTable = (
    prevItems: TreeItems<ITreeItem<IPackageProduct>>,
    listQuoteItems: IProductItem[],
    selectedId?: string,
    selectedIdVariant?: string
  ) => {
    const newItems = [...prevItems];

    let indexProduct = newItems.findIndex(
      (item) =>
        item?.data?.inventoryId === selectedId &&
        (item?.data?.variantId || EMPTY_STRING) === (selectedIdVariant || EMPTY_STRING)
    );

    const dataFiltered = selectedId ? newItems.filter((item) => item?.data?.inventoryId !== selectedId) : newItems;

    const newProductItems: TreeItems<ITreeItem<IPackageProduct>> = listQuoteItems
      .filter((item) => Number(item.quantity) > 0 && item.id === selectedId)
      .map((item) => {
        const existItem = newItems.find(
          (itemExist) =>
            itemExist?.data?.inventoryId === item.id &&
            (itemExist?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING)
        );

        return {
          id: uid(),
          level: 1,
          canHaveChildren: false,
          data: {
            inventoryId: item.id || EMPTY_STRING,
            name: item.name || EMPTY_STRING,
            variantName: item?.variantName || EMPTY_STRING,
            quantity: item.quantity || 0,
            laborCost: item.laborCost || EMPTY_STRING,
            rentalPrice: item.rentalPrice || EMPTY_STRING,
            variantId: item?.variantId || EMPTY_STRING,
            type: item?.type || EMPTY_STRING,
            inventoryImages: item.inventoryImages || [],
            isRequired: existItem?.data?.isRequired !== undefined ? existItem?.data?.isRequired : false,
            isShowPrice: existItem?.data?.isShowPrice !== undefined ? existItem?.data?.isShowPrice : false,
          },
        };
      });

    if (indexProduct !== -1) {
      dataFiltered.splice(indexProduct, 0, ...newProductItems);
    } else {
      dataFiltered.push(...newProductItems);
    }

    return dataFiltered;
  };

  const handleItemsChanged = (
    items: TreeItems<ITreeItem<IPackageProduct>>,
    reason: ItemChangedReason<ITreeItem<IPackageProduct>>
  ) => {
    if (reason.type === 'dropped') {
      const draggedItem = reason.draggedItem;
      const droppedToParent = reason.droppedToParent;

      const fromLevel = draggedItem.level;
      const toLevel = droppedToParent?.level ?? 0;

      if (fromLevel === 1 && droppedToParent) return;

      if (fromLevel > 1 && !droppedToParent) return;

      if (fromLevel === 1 && toLevel === 1) return;

      setItems(items);
    } else {
      setItems(items);
    }
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
  };
  //#endregion Handle Function

  return (
    <Stack id='setProductPackageFormComponent' className={cx('container')}>
      <Stack direction={'row'} justifyContent={'space-between'} mb={7}>
        <Stack direction={'row'} gap={1}>
          <Typography className={cx('header')}>{t('package_form_set_product_table_title')}</Typography>
          <Typography className={cx('required')}>{t('*')}</Typography>
        </Stack>
        <Typography className={cx('textTotal')}>{`${t(
          'package_form_set_product_table_title_total'
        )} ${formatCurrencyUSD({
          value: totalPrice,
          showDecimal: true,
        })}`}</Typography>
      </Stack>

      <Box className={cx('body')}>
        <GroupContext.Provider value={{ items, setItems }}>
          <DndContext onDragEnd={handleDragEnd}>
            <TableContainer className={cx('paper')}>
              <Table padding='none' size='medium'>
                <TableHead>
                  <TableRow className={cx('tableHeadRow')}>
                    <TableCell width={'28%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_name')}
                    </TableCell>

                    <TableCell width={'15%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_rental_price')}
                    </TableCell>

                    <TableCell width={'15%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_quantity')}
                    </TableCell>

                    <TableCell width={'15%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_subtotal')}
                    </TableCell>

                    <TableCell width={'12%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_required')}
                    </TableCell>

                    {/* <TableCell width={'13%'} className={cx('titleHead')}>
                      {t('package_form_set_product_table_title_show_price')}
                    </TableCell> */}

                    <TableCell width={'5%'} className={cx('titleHead')} align='center'>
                      {t('package_form_set_product_table_title_action')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                {items && items.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={6} align='center' className={cx('tableBodyCellNoData')}>
                        {t('common_no_data_available', { ns: 'common/base' })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>

              {items && items.length > 0 && (
                <SortableTree
                  items={items}
                  onItemsChanged={handleItemsChanged}
                  TreeItemComponent={ItemPackageProduct}
                  indentationWidth={25}
                />
              )}
            </TableContainer>
          </DndContext>
        </GroupContext.Provider>
      </Box>

      <FormHelperText className={cx('errorMessage')}>{errors.packageItems?.message}</FormHelperText>
    </Stack>
  );
};

export default SetProductPackageForm;
